import { ApiError, UnauthorizedApiError } from "~/errors"
import { getSmpLink, getTestQueryParams } from "~/utils"
import { getConfig } from "../config"
import {
  InterviewServiceGetQuizResponse,
  InterviewServiceSaveAnswersResponse,
} from "../generated/interview_service"
import type { ColorString, QuestionId, QuizId, URLString } from "../types"
import { getSaveBody } from "./getSaveBody"
export type { QuestionV2Type, QuizType } from "./QuestionType"

const defaultParams = {
  format: "json",
}

type SimpleObject = Record<string, string | undefined>

export const getInterviewId = () => {
  const configInstance = getConfig()["instance"]
  if (configInstance === "SUBSCRIPTION") {
    return "smp"
  }
  if (configInstance === "INTERVIEW2") {
    return "golden_thread_new"
  }
  return undefined
}

const getInternalParams = (): Record<string, string> => {
  const result: Record<string, string> = {}
  const interview_id = getInterviewId()
  if (interview_id) {
    result["interview_id"] = interview_id
  }

  const smp_link = getSmpLink()
  if (smp_link) {
    result["smp_link"] = smp_link
  }

  return result
}

const appendParamsApi = (url = "", additionalParams = {}) => {
  const parsed = new URLSearchParams(window.location.search)
  const user_id = parsed.get("user_id")
  const client = parsed.get("client") || "web"
  const params: Record<string, string> = {
    client,
    ...defaultParams,
    ...getInternalParams(),
    ...additionalParams,
    ...getTestQueryParams(),
  }
  if (!params["user_id"] && user_id) {
    params["user_id"] = user_id
  }
  const result = new URL(url)
  result.search = String(new URLSearchParams(params))
  return String(result)
}

const getUrl = (url: string, params: SimpleObject) => appendParamsApi(url, params)

// Universal Api Methods

const validateResponse = (resp: Response) => {
  if (resp.status === 401) {
    throw new UnauthorizedApiError(`Unauthorized request to ${resp.url}`)
  }

  if (!resp.ok) {
    throw new ApiError(`Response from ${resp.url} with status ${resp.status}`)
  }
}

const apiGet = async <T extends object>(url: URLString, params: SimpleObject = {}): Promise<T> => {
  const resp = await fetch(getUrl(url, params), {
    method: "GET",
    headers: { Accept: "application/json" },
  })
  validateResponse(resp)

  return (await resp.json()) as T
}

const apiPost = async (url: URLString, params: SimpleObject = {}, body: object) => {
  const resp = await fetch(getUrl(url, params), {
    method: "POST",
    mode: "cors",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })
  validateResponse(resp)

  /* TODO resp.json() here  */
  return resp
}
// End Universal Api Methods

export let sendLog = (_data: unknown): void => void 0

if (getConfig().endpoints.log) {
  sendLog = (data: unknown) => {
    navigator.sendBeacon(getConfig().endpoints.log, JSON.stringify(data))
  }
}

export const apiGetQuiz = async (
  quiz_id: QuizId,
  user_id: UserId
): Promise<InterviewServiceGetQuizResponse> => {
  const json = await apiGet(getConfig().endpoints.get_quiz, {
    quiz_id,
    user_id,
  })
  return InterviewServiceGetQuizResponse.fromJSON(json)
}

export const apiGetUser = async (): Promise<string> => {
  const json = await apiGet<{ userID?: string }>(getConfig().endpoints.get_user_id)
  const userId: string | undefined = json?.userID
  if (userId) {
    return userId
  }

  throw new Error("Unknown UserId")
}

export const apiGetConfig = async () => apiGet<ReturnType<typeof getConfig>>(getConfig().config_url)

export const apiSave = async (body: object): Promise<InterviewServiceSaveAnswersResponse> => {
  const res = await apiPost(getConfig().endpoints.save_answers, undefined, body)
  const json = await res.json()
  return InterviewServiceSaveAnswersResponse.fromJSON(json)
}

export const apiValidateEmail = async (
  userId: UserId,
  quizId: QuizId,
  questionId: QuestionId,
  email: string
): Promise<InterviewServiceSaveAnswersResponse> =>
  apiSave(getSaveBody(userId, quizId, { [questionId]: [email] }))

export const apiUpdateUserId = async (
  oldUserId: UserId,
  newUserId: UserId
): Promise<InterviewServiceSaveAnswersResponse> => {
  // TODO use UpdateUserIDRequest here
  const body = {
    oldUserId,
    request: { client: "web", userID: newUserId },
  }
  const res = await apiPost(getConfig().endpoints.update_user_id, undefined, body)
  const json = await res.json()
  return InterviewServiceSaveAnswersResponse.fromJSON(json)
}

// TODO use proto/inference.proto
type MaskTuple = [ColorString, Base64String]
export type FaceScanAnalysisResponse = {
  scanId: ScanId
  userId: UserId
  masks: Record<string, MaskTuple>
}

// https://airtable.com/appmKgar7eo8WFO5M/tblQeCys3Bv6oSRGl/viwDTPiwhiL50VCmL?blocks=hide
const COLORS: Record<string, ColorString> = {
  "acne_masks.acne_blackhead": "#578FFF",
  "acne_masks.acne_whitehead": "#578FFF",
  "acne_masks.acne_papule": "#578FFF",
  "acne_masks.acne_pustule": "#578FFF",

  "acne_masks.pores": "#58FF45",

  "wrinkles_masks.wrinkles": "#29EDED",
} as unknown as Record<string, ColorString>

const transformMask =
  (prefix: string) =>
  (obj: Record<string, string>): Record<string, MaskTuple> => {
    const result: Record<string, [ColorString, Base64String]> = {}
    Object.entries(obj).forEach(([_name, value]) => {
      const name = `${prefix}${_name}`
      const color = COLORS[name]
      if (color) {
        result[name] = [color, value as Base64String]
      }
    })
    return result
  }

export const faceScanAnalysis = async (
  userId: UserId,
  image: Base64String
): Promise<FaceScanAnalysisResponse> => {
  const res = await apiPost(
    getConfig().endpoints.face_scan_analysis,
    { raw_mask: "1", return_only_scan_id: "0" },
    { image_central_b64_data: image, user_id: userId }
  )
  const { scan_id: scanId, acne_masks, wrinkles_masks } = await res.json()
  const masks: Record<string, MaskTuple> = {
    ...transformMask("acne_masks.")(acne_masks),
    ...transformMask("wrinkles_masks.")(wrinkles_masks),
  }

  const result = {
    scanId,
    userId,
    masks,
  }
  return result
}

const DEFAULT_OPTIONS = {
  instant: false,
  webp: true,
  maxDimension: 0,
}
type Opts = typeof DEFAULT_OPTIONS

export const sendFileToImageStorage = async (
  file: File | Blob,
  opts: Partial<Opts> = DEFAULT_OPTIONS
) => {
  const url = new URL("https://api.pora.ai/upload")
  const {
    instant = DEFAULT_OPTIONS.instant,
    webp = DEFAULT_OPTIONS.webp,
    maxDimension = DEFAULT_OPTIONS.maxDimension,
  } = opts
  if (maxDimension) {
    url.searchParams.append("max_dimension", String(maxDimension))
  }
  if (instant) {
    url.searchParams.append("instant", "1")
  }
  if (webp) {
    url.searchParams.append("webp", "1")
  }
  const resp = await fetch(String(url), {
    method: "POST",
    mode: "cors",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: file,
  })

  return resp.text()
}

export const apiSaveFaceScanUrl = (
  userId: UserId,
  quizId: QuizId,
  faceScanUrl: URLString,
  scanId: ScanId
): Promise<InterviewServiceSaveAnswersResponse> =>
  apiSave(getSaveBody(userId, quizId, { face_scan_url: [faceScanUrl], scan_id: [scanId] }))
